/**
 * Dashboard collector create form — inline "Nieuwe Collector" form.
 *
 * Replicated from the create flow in CollectorenTab.tsx (`renderCreateForm` +
 * `handleCreateCollector`, Igor accepted the duplication). On success the form
 * stays open, bumps the suggested name, clears m², and refreshes the store via
 * `fetchCollectoren`.
 */
import { useState } from 'react';
import type { CollectorPicking, CreateCollectorPayload } from '@/api/tasks';
import type { CadSysteemEnum } from '@/api/cadJson';
import { createCollector } from '@/api/tasks';
import { formatPickingOption } from '@/utils/collectors';
import { useProjectViewStore } from '@/stores/projectView';
import { useCollectorSystems } from '@/stores/systems';

export function DashboardCollectorCreateForm({
  projectId,
  existingBloks,
  existingVerdiepingen,
  pickings,
  initialName,
  onClose,
  onCreated,
}: {
  projectId: number;
  existingBloks: string[];
  existingVerdiepingen: string[];
  pickings: CollectorPicking[];
  initialName: string;
  onClose: () => void;
  /** Called after a successful create so the parent can refresh the fase mapping. */
  onCreated?: () => void;
}) {
  const fetchCollectoren = useProjectViewStore((s) => s.fetchCollectoren);

  // Seed the suggested name ONCE on mount (the form remounts each time it opens,
  // so useState(initialName) is the "first open only" semantics). Deliberately NOT
  // synced to initialName changes: after a create, fetchCollectoren grows the store
  // collector count, which would otherwise recompute the parent's suggestedName and
  // clobber a user-typed name. The local trailing-digit bump below is the only
  // post-create name update (mirrors the original CollectorenTab).
  const [formName, setFormName] = useState(initialName);
  const [formVerdieping, setFormVerdieping] = useState('+00');
  const [formBlok, setFormBlok] = useState('Hoofdgebouw');
  const [formM2, setFormM2] = useState('');
  const [formSysteem, setFormSysteem] = useState('tacker');
  // Options come from the Odoo-defined roster (stores/systems), not a list kept here.
  const systeemOptions = useCollectorSystems();
  const [formMateriaallijstId, setFormMateriaallijstId] = useState<number | null>(null);
  const [formKringenCount, setFormKringenCount] = useState('0');
  const [ackNoMateriaallijst, setAckNoMateriaallijst] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  async function handleCreateCollector() {
    if (!formName.trim() || !formVerdieping.trim()) {
      setCreateError('Naam en verdieping zijn verplicht');
      return;
    }

    if (pickings.length > 0 && !formMateriaallijstId) {
      setCreateError('Kies een materiaallijst');
      return;
    }

    if (pickings.length === 0 && !ackNoMateriaallijst) {
      setCreateError('Bevestig dat er nog geen materiaallijst is');
      return;
    }

    const numKringen = parseInt(formKringenCount) || 0;
    const kringen = Array.from({ length: numKringen }, (_, i) => ({
      x_kringnummer: String(i + 1),
      x_system: formSysteem,
      x_legpatroon: '',
      x_length: 0,
    }));

    setCreating(true);
    setCreateError(null);
    try {
      await createCollector({
        project_id: projectId,
        name: formName.trim(),
        verdieping: formVerdieping.trim(),
        blok: formBlok.trim(),
        collector_m2: parseFloat(formM2) || 0,
        systeem: formSysteem as CadSysteemEnum,
        ...(formMateriaallijstId ? { materiaallijst_id: formMateriaallijstId } : {}),
        kringen: kringen as CreateCollectorPayload['kringen'],
      });
      // Success: keep form open, increment name, keep verdieping/blok/systeem, clear m².
      const nextName = formName.trim().replace(/(\d+)$/, (_, num) => String(Number(num) + 1));
      setFormName(nextName === formName.trim() ? formName.trim() + ' 2' : nextName);
      setFormM2('');
      setCreateError(null);
      setAckNoMateriaallijst(false);
      fetchCollectoren(projectId);
      onCreated?.(); // refresh the parent's fase mapping (new collector grouping)
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Collector aanmaken mislukt');
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="bg-white rounded-xl border border-td-blue/20 shadow-md p-5" onClick={(e) => e.stopPropagation()}>
      <h3 className="text-sm font-bold text-td-navy mb-4">Nieuwe Collector</h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label htmlFor="dash-collector-create-naam" className="block text-xs font-semibold text-td-muted mb-1">Naam</label>
          <input id="dash-collector-create-naam" type="text" value={formName} onChange={(e) => setFormName(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-td-blue" />
        </div>

        <div>
          <label className="block text-xs font-semibold text-td-muted mb-1">Verdieping</label>
          <div className="flex items-center gap-1.5">
            {[...new Set(['+00', '+01', '+02', '+03', '-01', ...existingVerdiepingen])].sort().map((v) => (
              <button key={v} type="button" onClick={() => setFormVerdieping(v)}
                className={`px-2.5 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${formVerdieping === v ? 'bg-td-blue text-white border-td-blue' : 'border-gray-300 text-td-navy hover:bg-gray-50'}`}
              >{v}</button>
            ))}
            <input type="text" value={formVerdieping} onChange={(e) => setFormVerdieping(e.target.value)}
              placeholder="Anders..." className="flex-1 min-w-[80px] px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-td-blue" />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-td-muted mb-1">Blok</label>
          <div className="flex items-center gap-1.5">
            {[...new Set(['Hoofdgebouw', ...existingBloks])].sort().map((b) => (
              <button key={b} type="button" onClick={() => setFormBlok(b)}
                className={`px-2.5 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${formBlok === b ? 'bg-td-blue text-white border-td-blue' : 'border-gray-300 text-td-navy hover:bg-gray-50'}`}
              >{b}</button>
            ))}
            <input type="text" value={formBlok} onChange={(e) => setFormBlok(e.target.value)}
              placeholder="Anders..." className="flex-1 min-w-[80px] px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-td-blue" />
          </div>
        </div>

        <div>
          <label htmlFor="dash-collector-create-m2" className="block text-xs font-semibold text-td-muted mb-1">m&sup2;</label>
          <input id="dash-collector-create-m2" type="number" step="0.1" min="0" value={formM2} onChange={(e) => setFormM2(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-td-blue" />
        </div>

        <div>
          <label className="block text-xs font-semibold text-td-muted mb-1">Systeem</label>
          <div className="flex items-center gap-1.5 flex-wrap">
            {systeemOptions.map((s) => (
              <button key={s.code} type="button" onClick={() => setFormSysteem(s.code)}
                className={`px-2.5 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${formSysteem === s.code ? 'bg-td-blue text-white border-td-blue' : 'border-gray-300 text-td-navy hover:bg-gray-50'}`}
              >{s.label}</button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-td-muted mb-1">Aantal kringen</label>
          <div className="flex items-center gap-1.5 flex-wrap">
            {Array.from({ length: 16 }, (_, i) => String(i)).map((n) => (
              <button key={n} type="button" onClick={() => setFormKringenCount(n)}
                className={`w-8 h-8 text-xs font-semibold rounded-lg border transition-colors ${formKringenCount === n ? 'bg-td-blue text-white border-td-blue' : 'border-gray-300 text-td-navy hover:bg-gray-50'}`}
              >{n}</button>
            ))}
            <input type="number" min="0" value={formKringenCount} onChange={(e) => setFormKringenCount(e.target.value)}
              className="w-16 px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-td-blue" />
          </div>
        </div>
      </div>

      {pickings.length > 0 && (
        <div className="mt-4">
          <label htmlFor="dash-collector-create-materiaallijst" className="block text-xs font-semibold text-td-muted mb-1">Materiaallijst <span className="text-red-600">*</span></label>
          <select
            id="dash-collector-create-materiaallijst"
            required
            value={formMateriaallijstId ?? ''}
            onChange={(e) => setFormMateriaallijstId(Number(e.target.value) || null)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-td-blue bg-white"
          >
            <option value="" disabled>Kies een materiaallijst...</option>
            {pickings.map((p) => (
              <option key={p.id} value={p.id}>{formatPickingOption(p)}</option>
            ))}
          </select>
        </div>
      )}

      {pickings.length === 0 && (
        <div className="mt-4">
          <label className="flex items-start gap-2 text-xs font-semibold text-td-navy">
            <input
              type="checkbox"
              checked={ackNoMateriaallijst}
              onChange={(e) => setAckNoMateriaallijst(e.target.checked)}
              className="mt-0.5"
            />
            <span>Materiaallijst nog niet beschikbaar — later koppelen</span>
          </label>
        </div>
      )}

      {createError && (
        <div className="mt-3 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{createError}</div>
      )}

      <div className="mt-4 flex items-center gap-3">
        <button type="button" onClick={handleCreateCollector} disabled={creating}
          className="px-4 py-2 text-sm font-semibold bg-td-blue text-white rounded-lg hover:bg-td-blue/90 transition disabled:opacity-50">
          {creating ? 'Aanmaken...' : 'Collector Aanmaken'}
        </button>
        <button type="button" onClick={onClose}
          className="px-4 py-2 text-sm font-semibold text-td-muted hover:text-td-navy transition">
          Annuleren
        </button>
      </div>
    </div>
  );
}
